import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RETOUCH_EXPECTED_MS, RETOUCH_RETRY_MS, RETOUCH_STALE_MS, isRetouchInProgress } from '@meniudigital/shared'
import { State } from 'src/state'
import { getProducts } from 'src/state/products'

// A retouch takes up to a minute. Instead of polling, the products are refetched once when the
// oldest running retouch reaches the one minute mark, once more at a minute and a half, and a last
// time at the stale mark, where the server treats it as failed: a job that failed late is shown as
// such instead of sitting at 98% until the next data refresh.
const SLACK_MS = 1000

export default function useRetouchRefresh() {
  const dispatch = useDispatch()
  const { list } = useSelector((state: State) => state.products)
  const [refetchCount, setRefetchCount] = useState(0)

  const startTimes = list
    .filter(product => product.isRetouchEnabled !== false && isRetouchInProgress(product))
    .map(product => new Date(product.retouchStartedAt!).getTime())
  const oldestStart = startTimes.length ? Math.min(...startTimes) : 0

  useEffect(() => {
    if (!oldestStart) {
      return
    }

    const refetch = () => {
      dispatch(getProducts())
      setRefetchCount(count => count + 1)
    }

    const elapsed = Date.now() - oldestStart
    const checkpoints = [RETOUCH_EXPECTED_MS, RETOUCH_RETRY_MS, RETOUCH_STALE_MS]
    // A second of slack, so a refetch landing just before a mark does not schedule another one right away
    const nextCheckpoint = checkpoints.find(checkpoint => checkpoint > elapsed + SLACK_MS)

    if (!nextCheckpoint) {
      return
    }

    // The refetch lands just after the mark, never before it: at the stale mark the server only gives up
    // on the job once it is truly past, so asking a second early brings back the same "in progress" product
    const timer = setTimeout(refetch, nextCheckpoint - elapsed + SLACK_MS)

    // Coming back to the tab after the two minute mark should show the result right away
    const onVisibilityChange = () => {
      if (!document.hidden && Date.now() - oldestStart >= RETOUCH_EXPECTED_MS) {
        clearTimeout(timer)
        refetch()
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [oldestStart, refetchCount])
}
